import React from "react";
import useApi from "@/hooks/useApi";
import { PrimaryBlueButton } from "@velaro/velaro-component-lib";
import LoadingSpinner from "@/components/LoadingSpinner";
import InstagramEditModal from "./InstagramEditModal";

interface InstagramConfig {
  id: number;
  instagramAccountId: string;
  username: string;
  displayName: string;
  profilePictureUrl: string;
  pageName: string;
  teamId: number | null;
  teamName: string | null;
  autoCreateContact: boolean;
}

// FB SDK types
declare global {
  interface Window {
    FB: any;
    fbAsyncInit: () => void;
  }
}

export default function InstagramIntegration() {
  const [loading, setLoading] = React.useState(true);
  const [configs, setConfigs] = React.useState<InstagramConfig[]>([]);
  const [editId, setEditId] = React.useState<number | undefined>();
  const [provisioning, setProvisioning] = React.useState(false);
  const [message, setMessage] = React.useState<{ text: string; type: "success" | "error" } | null>(null);
  const [sdkLoaded, setSdkLoaded] = React.useState(false);
  const api = useApi();

  const fetchConfigs = React.useCallback(async () => {
    try {
      const resp = await api.messaging.get("Instagram/Integrations");
      const data = await resp.json();
      setConfigs(Array.isArray(data) ? data : []);
    } catch {
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  }, [api.messaging]);

  React.useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  // Load Meta SDK (same one used by Facebook + WhatsApp)
  React.useEffect(() => {
    if ((window as any).FB) {
      setSdkLoaded(true);
      return;
    }

    const script = document.createElement("script");
    script.id = "facebook-jssdk";
    script.src = "https://connect.facebook.net/en_US/sdk.js";
    script.addEventListener("load", () => setSdkLoaded(true));
    document.body.appendChild(script);
  }, []);

  React.useEffect(() => {
    if (!sdkLoaded) return;
    const appId = import.meta.env.VITE_FACEBOOK_APP_ID;
    if (!appId) return;

    (window as any).FB.init({
      appId,
      cookie: true,
      xfbml: true,
      version: "v20.0"
    });
  }, [sdkLoaded]);

  const handleConnect = React.useCallback(() => {
    if (!sdkLoaded || !(window as any).FB) {
      setMessage({ text: "Meta SDK not loaded. Please refresh and try again.", type: "error" });
      return;
    }

    // instagram_manage_messages requires App Review from Meta
    // Pages scopes are needed to get the linked Instagram Business account
    (window as any).FB.login(
      async (response: any) => {
        if (response.status !== "connected") {
          setMessage({ text: "Authorization cancelled or failed.", type: "error" });
          return;
        }

        setProvisioning(true);
        setMessage(null);

        try {
          const resp = await api.messaging.post("Instagram/Provision", {
            userId: response.authResponse.userID,
            userAccessToken: response.authResponse.accessToken
          });

          const data = await resp.json();

          if (!resp.ok) {
            setMessage({ text: data.error ?? "Provisioning failed.", type: "error" });
            return;
          }

          setMessage({ text: data.message ?? "Instagram accounts connected.", type: "success" });
          fetchConfigs();
        } catch {
          setMessage({ text: "Failed to connect Instagram. Please try again.", type: "error" });
        } finally {
          setProvisioning(false);
        }
      },
      {
        scope:
          "pages_show_list,pages_messaging,instagram_manage_messages,instagram_basic,public_profile,email"
      }
    );
  }, [sdkLoaded, api.messaging, fetchConfigs]);

  const handleRemove = React.useCallback(
    async (id: number) => {
      if (!window.confirm("Remove this Instagram account from Velaro?")) return;
      try {
        await api.messaging.delete(`Instagram/Integrations/${id}`);
        fetchConfigs();
      } catch {
        setMessage({ text: "Failed to remove account. Please try again.", type: "error" });
      }
    },
    [api.messaging, fetchConfigs]
  );

  if (loading) return <div className="flex justify-center py-20"><LoadingSpinner /></div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <img src="/img/meta-instagram.svg" alt="Instagram" className="w-10 h-10 rounded-lg flex-shrink-0" />
        <div>
          <h2 className="text-lg font-semibold text-slate-800">Instagram Direct Messages</h2>
          <p className="text-sm text-slate-500">
            Connect Instagram Business accounts to receive and respond to DMs in the Velaro inbox.
            Requires a Business or Creator Instagram account linked to a Facebook Page.
          </p>
        </div>
      </div>

      {/* Status message */}
      {message && (
        <div
          className={`px-4 py-3 rounded-md text-sm ${
            message.type === "success"
              ? "bg-green-50 border border-green-200 text-green-800"
              : "bg-red-50 border border-red-200 text-red-800"
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Connected accounts table */}
      {configs.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full bg-white border border-slate-200 rounded-lg text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Account</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Facebook Page</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Team</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Status</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600" style={{ width: 1 }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {configs.map((c) => (
                <tr key={c.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {c.profilePictureUrl ? (
                        <img
                          src={c.profilePictureUrl}
                          alt={c.username}
                          className="w-8 h-8 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                          <i className="fab fa-instagram text-white text-xs" aria-hidden="true" />
                        </div>
                      )}
                      <div>
                        <div className="font-medium text-slate-800">{c.displayName || c.username}</div>
                        {c.username && (
                          <div className="text-xs text-slate-500">@{c.username}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{c.pageName || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{c.teamName ?? (c.teamId ? `Team ${c.teamId}` : "All teams")}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                      Active
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <button
                      className="text-blue-600 hover:text-blue-800 underline text-xs"
                      onClick={() => setEditId(c.id)}
                      aria-label={`Edit ${c.username}`}
                    >
                      Edit
                    </button>
                    <span className="mx-2 text-slate-300">|</span>
                    <button
                      className="text-red-500 hover:text-red-700 underline text-xs"
                      onClick={() => handleRemove(c.id)}
                      aria-label={`Remove ${c.username}`}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {configs.length === 0 && !loading && (
        <div className="text-center py-10 bg-slate-50 rounded-lg border border-dashed border-slate-200">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center mx-auto mb-3">
            <i className="fab fa-instagram text-pink-400 text-xl" aria-hidden="true" />
          </div>
          <p className="text-slate-600 font-medium">No Instagram accounts connected</p>
          <p className="text-slate-400 text-sm mt-1">
            Connect an Instagram Business account to receive DMs in your inbox.
          </p>
        </div>
      )}

      {/* Connect button */}
      <div>
        <PrimaryBlueButton
          label={provisioning ? "Connecting..." : "Connect Instagram Account"}
          onClick={handleConnect}
          disabled={provisioning}
        />
        <p className="text-xs text-slate-400 mt-2">
          You'll be prompted to log in with Facebook and authorize Velaro to access your Instagram
          Business account's messages. Your Instagram account must be a Business or Creator account
          linked to a Facebook Page.
        </p>
      </div>

      {editId !== undefined && (
        <InstagramEditModal
          configId={editId}
          onClose={() => setEditId(undefined)}
          onSave={() => {
            setEditId(undefined);
            fetchConfigs();
          }}
        />
      )}
    </div>
  );
}
